import React, { useEffect, useMemo, useRef, useState } from 'react';
import produce from 'immer';
import range from 'lodash/range';
import { Controller, FormProvider, useFieldArray, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import { useSolvSelector } from '~/reducers';
import history from '../../core/history/index';

import {
  getAuxPatients,
  getSpecifiedNumberofAuxPatients,
  setAuxPatientInfo,
} from '../../ducks/bookingWidgetFamilyBooking';
import TextField from './TextField';
import { colors } from '~/constants/colors';
import {
  ADDRESS_FIELD_NAME,
  ADDRESS_SECONDARY_FIELD_NAME,
  BIRTH_SEX_FIELD_NAME,
  CITY_FIELD_NAME,
  ELECTRONIC_COMMUNICATION_CONSENT_FIELD_NAME,
  EMAIL_FIELD_NAME,
  FIRST_NAME_FIELD_NAME,
  LAST_NAME_FIELD_NAME,
  PHONE_FIELD_NAME,
  STATE_CODE_FIELD_NAME,
  SYMPTOMS_FIELD_NAME,
  TELEMED_CONSENT_FIELD_NAME,
  TOS_CONSENT_FIELD_NAME,
  ZIP_CODE_FIELD_NAME,
} from '../BookingWidget/util/validators';
import SelectField from './SelectField';
import { InsurancePricingFootnote } from '../BookingWidget/components';
import Button, { ButtonVariations } from '../SolvPatternLibrary/Button';
import { getButtonText } from '../BookingWidget/util';
import CheckBoxField from './CheckBoxField';
import ConsentLabelBookingWidget, {
  BookingWidgetSMSConsentLabel,
  TeleMedConsentLabel,
} from '../SolvPatternLibrary/Form/ConsentLabelBookingWidget';
import { FieldContainer } from './FieldContainer';
import {
  BIRTH_SEX_FEMALE,
  BIRTH_SEX_MALE,
  BIRTH_SEX_OTHER,
  FETCH_LOCATION,
  TOS_TERMS_ERROR_MESSAGE,
  TOS_TERMS_PRIVACY_ERROR_MESSAGE,
  TYPE_SELF,
  TYPE_SKIP,
} from '~/constants';
import HomeAddress from './HomeAddress';
import { getDays, getMonths } from '../SolvPatternLibrary/BirthDateInput/util';
import HowDoYouWantToPayToggle from './HowDoYouWantToPayToggle';
import { getNewBooking } from '~/selectors/newBooking';
import { setBookingProfileConsent, setBookingProfilePayment } from '~/actions/newBooking';
import { ADD_INSURANCE, DECIDE_LATER_PHOTO_ID_UPLOAD } from '~/routes/addInsurance/constants';
import { InsuranceFormType } from '~/routes/booking-widget';
import { insuranceFormSubmission } from '~/actions/insurance';
import {
  isBirthSexRequired,
  isBookingWidgetAddressRequired,
  isExternalTelemedLocation,
  isInsuranceDisabled,
  isPhotoIdUploadEnabledForEntrypoint,
  isTelemedLocation,
} from '~/core/util/location';
import {
  PHOTO_ID_UPLOAD,
  PHOTO_ID_UPLOAD_ORIGIN_BOOKING_WIDGET,
} from '~/core/util/bookingFlowRouting';
import { smallerThanOrEqualTo } from '~/core/util/styledComponents';
import { standardizeYear } from '~/core/util/date';
import { validateDob } from '~/core/util/form';

const AdditionalPatientsContainer = styled.div.withConfig({ componentId: 'sc-vfdeh5-0' })`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const AdditionalPatientsColumn = styled.div.withConfig({ componentId: 'sc-vfdeh5-1' })`
  max-width: 480px;
  width: 100%;
  margin-bottom: 30px;
`;

const AdditionalPatientsPageHeading = styled.div.withConfig({ componentId: 'sc-vfdeh5-2' })`
  font-weight: 400;
  font-size: 24px;
  line-height: 24px;
  color: ${colors.squid};
  margin-top: 48px;
  display: flex;
  justify-content: center;
`;

const FormHeading = styled.div.withConfig({ componentId: 'sc-vfdeh5-3' })`
  font-weight: 400;
  font-size: 15px;
  text-transform: uppercase;
  color: ${colors.grey600};
  margin-bottom: 8px;
  ${smallerThanOrEqualTo.md`
    margin-left: 16px;
  `}
`;
const AdditionalPatientForm = styled.div.withConfig({ componentId: 'sc-vfdeh5-4' })`
  text-align: start;
  margin-top: 32px;
`;

const FormFooter = styled.div.withConfig({ componentId: 'sc-vfdeh5-5' })`
  display: flex;
  flex-direction: column;
`;

const InsurancePricingFootnoteWrapper = styled.div.withConfig({ componentId: 'sc-vfdeh5-6' })`
  margin-bottom: 30px;
`;

const StyledButton = styled(Button).withConfig({ componentId: 'sc-vfdeh5-7' })`
  margin-top: 20px;
  align-self: center;
`;

const DateOfBirthSectionContainer = styled.div.withConfig({ componentId: 'sc-vfdeh5-8' })`
  display: flex;
  ${FieldContainer} {
    border-radius: 0;
    flex: 1;

    &:first-child {
      border-right: none;
      border-top: none;
      flex: 1.5;
    }

    &:last-child {
      border-left: none;
      border-bottom-width: 1px;
    }

    select {
      width: 100%;
      border: none;
    }
  }
`;

const ConsentContainer = styled.div.withConfig({ componentId: 'sc-vfdeh5-9' })`
  ${smallerThanOrEqualTo.md`
    margin-left: 16px;
  `}

  & > *:not(:last-child) {
    margin-bottom: 15px;
  }
`;

export interface AdditionalPatientsFormState {
  auxPatients: {
    id: number;
    [FIRST_NAME_FIELD_NAME]: string;
    [LAST_NAME_FIELD_NAME]: string;
    [PHONE_FIELD_NAME]: string;
    [EMAIL_FIELD_NAME]: string;
    [SYMPTOMS_FIELD_NAME]: string;
    [BIRTH_SEX_FIELD_NAME]: string;
    [ADDRESS_FIELD_NAME]: string;
    [ADDRESS_SECONDARY_FIELD_NAME]: string;
    [CITY_FIELD_NAME]: string;
    [STATE_CODE_FIELD_NAME]: string;
    [ZIP_CODE_FIELD_NAME]: string;
    birthDateMonth: string;
    birthDateDay: string;
    birthDateYear: string;
    hasDifferentAddress: boolean;
  }[];
  [TOS_CONSENT_FIELD_NAME]: boolean;
  [TELEMED_CONSENT_FIELD_NAME]: boolean;
  [ELECTRONIC_COMMUNICATION_CONSENT_FIELD_NAME]: boolean;
  payment: string;
}

export type IndividualPatientFields = AdditionalPatientsFormState['auxPatients'][number];

const AUX_PATIENTS_ARRAY_NAME = 'auxPatients';

const INITIAL_AUX_PATIENT_FORM_VALUES: IndividualPatientFields = {
  id: 0,
  [FIRST_NAME_FIELD_NAME]: '',
  [LAST_NAME_FIELD_NAME]: '',
  [PHONE_FIELD_NAME]: '',
  [EMAIL_FIELD_NAME]: '',
  [SYMPTOMS_FIELD_NAME]: '',
  [BIRTH_SEX_FIELD_NAME]: '',
  [ADDRESS_FIELD_NAME]: '',
  [ADDRESS_SECONDARY_FIELD_NAME]: '',
  [CITY_FIELD_NAME]: '',
  [STATE_CODE_FIELD_NAME]: '',
  [ZIP_CODE_FIELD_NAME]: '',
  birthDateMonth: '',
  birthDateDay: '',
  birthDateYear: '',
  hasDifferentAddress: false,
};

type IndividualPatientFieldName<F extends keyof IndividualPatientFields> =
  `${typeof AUX_PATIENTS_ARRAY_NAME}.${number}.${F}`;

export function getPatientFieldName<F extends keyof IndividualPatientFields>(
  patientIndex: number,
  fieldName: F
): IndividualPatientFieldName<F> {
  return `${AUX_PATIENTS_ARRAY_NAME}.${patientIndex}.${fieldName}` as IndividualPatientFieldName<F>;
}

const DateOfBirthSection = (props: { vertical?: boolean; children: React.ReactNode }) => {
  if (props.vertical) {
    return <>{props.children}</>;
  }

  return <DateOfBirthSectionContainer>{props.children}</DateOfBirthSectionContainer>;
};

const AdditionalPatients = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const dispatch = useDispatch();
  const location = useSolvSelector((state) => state.location);
  const locale = useSelector((state: any) => state.newBooking?.locale);
  const specifiedNumberOfAuxPatients = useSolvSelector(getSpecifiedNumberofAuxPatients);
  const auxPatientsFromRedux = useSolvSelector(getAuxPatients);
  const newBooking = useSolvSelector(getNewBooking);
  const bookingWidgetAddressRequired = useSolvSelector((state) =>
    isBookingWidgetAddressRequired(state.location)
  );
  const initAuxPatients: IndividualPatientFields[] = useMemo(() => {
    return range(specifiedNumberOfAuxPatients).map((_: any, index: number) => {
      if (auxPatientsFromRedux[index]) {
        let auxPatient = {
          ...INITIAL_AUX_PATIENT_FORM_VALUES,
          ...auxPatientsFromRedux[index],
          id: index,
        };

        const birthDate = auxPatientsFromRedux[index]?.birthDate;
        if (birthDate) {
          const momentBirthDate = moment(birthDate);
          auxPatient = {
            ...auxPatient,
            birthDateMonth: momentBirthDate.format('MMMM'),
            birthDateDay: momentBirthDate.format('D'),
            birthDateYear: momentBirthDate.format('YYYY'),
          };
        }
        return auxPatient;
      }
      return {
        ...INITIAL_AUX_PATIENT_FORM_VALUES,
        id: index,
      };
    });
  }, [specifiedNumberOfAuxPatients, auxPatientsFromRedux]);

  const bookingDefaultPaymentMethod = newBooking?.profile?.payment || 'insurance';
  const defaultValues: AdditionalPatientsFormState = useMemo(
    () => ({
      [AUX_PATIENTS_ARRAY_NAME]: initAuxPatients,
      [TOS_CONSENT_FIELD_NAME]: false,
      [TELEMED_CONSENT_FIELD_NAME]: false,
      [ELECTRONIC_COMMUNICATION_CONSENT_FIELD_NAME]: false,
      payment: bookingDefaultPaymentMethod,
    }),
    [initAuxPatients, bookingDefaultPaymentMethod]
  );

  const methods = useForm<AdditionalPatientsFormState>({
    defaultValues,
  });

  const { reset, handleSubmit, control, setValue, getValues, clearErrors } = methods;

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const hasFetchedLocation = useRef(false);

  useEffect(() => {
    if (!hasFetchedLocation.current && newBooking?.booking?.locationId && isEmpty(location)) {
      dispatch({
        type: FETCH_LOCATION,
        locationId: newBooking?.booking?.locationId,
      });
      hasFetchedLocation.current = true;
    }
  }, [newBooking, location, dispatch]);

  const { fields: patientsArray } = useFieldArray<AdditionalPatientsFormState>({
    control,
    name: AUX_PATIENTS_ARRAY_NAME,
  });

  const validateDateOfBirth = (index: number) => {
    const yearFieldName = getPatientFieldName(index, 'birthDateYear');
    const monthFieldName = getPatientFieldName(index, 'birthDateMonth');
    const dayFieldName = getPatientFieldName(index, 'birthDateDay');
    const year = getValues(yearFieldName);
    const month = getValues(monthFieldName);
    const day = getValues(dayFieldName);
    const dobErrors = validateDob(month, day, year);
    if (isEmpty(dobErrors)) {
      clearErrors([yearFieldName, monthFieldName, dayFieldName]);
    }
    return isEmpty(dobErrors);
  };

  const { t: bookingWidgetTranslate } = useTranslation(['common', 'bookingWidget']);

  const handleOnContinueClick = (data: AdditionalPatientsFormState) => {
    setIsSubmitting(true);
    let { auxPatients, tosConsent, telemedConsent, electronicCommunicationConsent, payment } = data;

    // If bookingWidgetAddressRequired, we need to check for consistency of aux bookings
    // with primary patient.
    auxPatients = produce(auxPatients, (draft) => {
      if (bookingWidgetAddressRequired) {
        for (const patient of draft) {
          if (!patient.hasDifferentAddress) {
            patient[ADDRESS_FIELD_NAME] = newBooking.profile.address_street || '';
            patient[ADDRESS_SECONDARY_FIELD_NAME] =
              newBooking.profile.address_street_secondary || '';
            patient[CITY_FIELD_NAME] = newBooking.profile.address_city || '';
            patient[STATE_CODE_FIELD_NAME] = newBooking.profile.address_state || '';
            patient[ZIP_CODE_FIELD_NAME] = newBooking.profile.address_zip || '';
          }
        }
      } else {
        for (const patient of draft) {
          patient[ADDRESS_FIELD_NAME] = '';
          patient[ADDRESS_SECONDARY_FIELD_NAME] = '';
          patient[CITY_FIELD_NAME] = '';
          patient[STATE_CODE_FIELD_NAME] = '';
          patient[ZIP_CODE_FIELD_NAME] = '';
        }
      }
    });

    dispatch(setAuxPatientInfo(auxPatients));
    dispatch(setBookingProfilePayment(payment));
    dispatch(
      setBookingProfileConsent({ tosConsent, telemedConsent, electronicCommunicationConsent })
    );

    const includePaymentPage = location.isPaymentsEnabled && location.isPaymentDuringBookingEnabled;
    const primaryPatient = newBooking?.profile;
    if (payment === TYPE_SELF || payment === TYPE_SKIP || isInsuranceDisabled(location as any)) {
      // Prepare and dispatch insurance information
      const insuranceForm: InsuranceFormType = {
        insurerType: isInsuranceDisabled(location as any) ? TYPE_SKIP : payment,
        firstName: primaryPatient.firstName,
        lastName: primaryPatient.lastName,
      };

      dispatch(insuranceFormSubmission(insuranceForm));

      if (isPhotoIdUploadEnabledForEntrypoint(location as any, 'bookingWidget')) {
        if (includePaymentPage) {
          if (insuranceForm.insurerType === TYPE_SKIP) {
            history.push(`/payments/add-card-bf/${DECIDE_LATER_PHOTO_ID_UPLOAD}`);
          } else {
            history.push(`/payments/add-card-bf${PHOTO_ID_UPLOAD}`);
          }
          return;
        }
        history.push(`${PHOTO_ID_UPLOAD}/${PHOTO_ID_UPLOAD_ORIGIN_BOOKING_WIDGET}`);
        return;
      }
      history.push('/submitBooking');
      return;
    }

    history.push(`/${ADD_INSURANCE}/bw`);
    return;
  };

  const birthSexOptions = useMemo(() => {
    return [
      {
        key: BIRTH_SEX_MALE.toLowerCase(),
        label: bookingWidgetTranslate('common:userInfo.sexMale'),
        value: BIRTH_SEX_MALE.toLowerCase(),
      },
      {
        key: BIRTH_SEX_FEMALE.toLowerCase(),
        label: bookingWidgetTranslate('common:userInfo.sexFemale'),
        value: BIRTH_SEX_FEMALE.toLowerCase(),
      },
      {
        key: BIRTH_SEX_OTHER.toLowerCase(),
        label: bookingWidgetTranslate('common:userInfo.sexOther'),
        value: BIRTH_SEX_OTHER.toLowerCase(),
      },
    ];
  }, [bookingWidgetTranslate]);

  const monthOptions = useMemo(() => {
    const months = getMonths(locale);
    months.shift();
    return months;
  }, [locale]);

  const dayOptions = useMemo(() => {
    const days = getDays();
    days.shift();
    return days;
  }, []);

  return (
    <AdditionalPatientsContainer>
      <AdditionalPatientsColumn>
        <AdditionalPatientsPageHeading>Additional Family Members</AdditionalPatientsPageHeading>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(handleOnContinueClick)}>
            {patientsArray.map((patientVal, index) => {
              return (
                <AdditionalPatientForm key={patientVal.id}>
                  <FormHeading>{`Additional Patient #${index + 1}`}</FormHeading>
                  <div>
                    <Controller
                      control={control}
                      name={getPatientFieldName(index, FIRST_NAME_FIELD_NAME)}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          error={!!fieldState.error}
                          placeholder="Patient first name"
                        />
                      )}
                      rules={{
                        required: true,
                      }}
                    />
                    <Controller
                      control={control}
                      name={getPatientFieldName(index, LAST_NAME_FIELD_NAME)}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          error={!!fieldState.error}
                          placeholder="Patient last name"
                        />
                      )}
                      rules={{
                        required: true,
                      }}
                    />
                    <DateOfBirthSection>
                      <Controller
                        control={control}
                        name={getPatientFieldName(index, 'birthDateMonth')}
                        render={({ field, fieldState }) => (
                          <SelectField
                            {...field}
                            error={!!fieldState.error}
                            options={monthOptions}
                            placeholder="Birth date: Month"
                          />
                        )}
                        rules={{
                          required: true,
                          validate: () => validateDateOfBirth(index),
                        }}
                      />
                      <Controller
                        control={control}
                        name={getPatientFieldName(index, 'birthDateDay')}
                        render={({ field, fieldState }) => (
                          <SelectField
                            {...field}
                            error={!!fieldState.error}
                            options={dayOptions}
                            placeholder="Day"
                          />
                        )}
                        rules={{
                          required: true,
                          validate: () => validateDateOfBirth(index),
                        }}
                      />
                      <Controller
                        control={control}
                        name={getPatientFieldName(index, 'birthDateYear')}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            error={!!fieldState.error}
                            maxLength={4}
                            onBlur={(value) => {
                              setValue(
                                getPatientFieldName(index, 'birthDateYear'),
                                // @ts-ignore
                                String(standardizeYear(value)),
                                {
                                  shouldValidate: true,
                                }
                              );
                            }}
                            placeholder="Year"
                            type="tel"
                          />
                        )}
                        rules={{
                          required: true,
                          validate: () => validateDateOfBirth(index),
                        }}
                      />
                    </DateOfBirthSection>
                    {isBirthSexRequired(location) && (
                      <Controller
                        control={control}
                        name={getPatientFieldName(index, BIRTH_SEX_FIELD_NAME)}
                        render={({ field, fieldState }) => (
                          <SelectField
                            {...field}
                            error={!!fieldState.error}
                            options={birthSexOptions}
                            placeholder="Sex at birth"
                          />
                        )}
                        rules={{
                          required: true,
                        }}
                      />
                    )}
                    <Controller
                      control={control}
                      name={getPatientFieldName(index, SYMPTOMS_FIELD_NAME)}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          error={!!fieldState.error}
                          placeholder="Reason for visit"
                        />
                      )}
                      rules={{
                        required: true,
                      }}
                    />
                  </div>
                  <HomeAddress index={index} />
                </AdditionalPatientForm>
              );
            })}

            <FormFooter>
              <Controller
                control={control}
                name="payment"
                render={({ field }) => <HowDoYouWantToPayToggle {...field} />}
                rules={{
                  required: true,
                }}
              />

              <InsurancePricingFootnoteWrapper>
                <InsurancePricingFootnote location={location} />
              </InsurancePricingFootnoteWrapper>

              <ConsentContainer>
                <Controller
                  control={control}
                  name={TOS_CONSENT_FIELD_NAME}
                  render={({ field, fieldState }) => (
                    <CheckBoxField
                      {...field}
                      error={fieldState.error}
                      label={<ConsentLabelBookingWidget />}
                    />
                  )}
                  rules={{
                    required: TOS_TERMS_PRIVACY_ERROR_MESSAGE,
                  }}
                />
                <Controller
                  control={control}
                  name={ELECTRONIC_COMMUNICATION_CONSENT_FIELD_NAME}
                  render={({ field, fieldState }) => (
                    <CheckBoxField
                      {...field}
                      error={fieldState.error}
                      label={<BookingWidgetSMSConsentLabel />}
                    />
                  )}
                  rules={{
                    required: TOS_TERMS_ERROR_MESSAGE,
                  }}
                />
                {isTelemedLocation(location) && !isExternalTelemedLocation(location) && (
                  <Controller
                    control={control}
                    name={TELEMED_CONSENT_FIELD_NAME}
                    render={({ field, fieldState }) => (
                      <CheckBoxField
                        {...field}
                        error={fieldState.error}
                        label={<TeleMedConsentLabel />}
                      />
                    )}
                    rules={{
                      required: TOS_TERMS_ERROR_MESSAGE,
                    }}
                  />
                )}
              </ConsentContainer>

              <StyledButton
                data-testid="additionalPatientsBookButton"
                disabled={isSubmitting}
                text={getButtonText(null, false, false, bookingWidgetTranslate)}
                type="submit"
                variation={ButtonVariations.pinkPrimary}
                width="190px"
              />
            </FormFooter>
          </form>
        </FormProvider>
      </AdditionalPatientsColumn>
    </AdditionalPatientsContainer>
  );
};

export default AdditionalPatients;
